import { useQuery } from '@tanstack/react-query';
import {
  AlarmClock,
  Activity,
  Banknote,
  BellRing,
  Coins,
  Crown,
  Gavel,
  HandCoins,
  Landmark,
  Lock,
  Smartphone,
  Sunrise,
  TrendingDown,
  UserPlus,
  Users as UsersIcon,
  Wallet,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { KeyboardEvent, ReactNode } from 'react';
import { PageHeader } from '../components/Layout';
import { TrendChart, type SeriesSpec } from '../components/TrendChart';
import {
  Card,
  CardHeader,
  ErrorNote,
  Skeleton,
  StatTile,
  cn,
} from '../components/ui';
import { api } from '../lib/api';
import {
  formatCompact,
  formatCredits,
  formatDuration,
  formatMoneyCompact,
  formatNumber,
  formatPlatform,
  formatRate,
  humanize,
  percentOf,
  timezoneOffsetMinutes,
} from '../lib/format';
import { useT, type Translate } from '../lib/i18n';
import type {
  ActivityMetric,
  ActivityScope,
  BreakdownRow,
  DashboardOverview,
} from '../lib/types';
import { ActivityDrawer, parseMetric } from './dashboard/ActivityDrawer';

/**
 * Opens the list of people behind a number. Passed down rather than reached
 * for through a context: three sections use it, and a prop makes it obvious
 * which tiles are questions and which are statements.
 */
type Drill = (metric: ActivityMetric, scope?: ActivityScope) => void;

const TREND_DAYS = 14;

/** Fixed slot order. A series keeps its hue whatever else is on the chart. */
const wakeSeries = (t: Translate): SeriesSpec[] => [
  { key: 'earned', label: t('dashboard.series.wokeUp'), color: 'var(--series-1)' },
  { key: 'missed', label: t('dashboard.series.missed'), color: 'var(--series-2)' },
];

const creditSeries = (t: Translate): SeriesSpec[] => [
  {
    key: 'toppedUp',
    label: t('dashboard.series.toppedUp'),
    color: 'var(--series-1)',
  },
  {
    key: 'forfeited',
    label: t('dashboard.series.forfeited'),
    color: 'var(--series-2)',
  },
  {
    key: 'paidOut',
    label: t('dashboard.series.paidOut'),
    color: 'var(--series-3)',
  },
];

const growthSeries = (t: Translate): SeriesSpec[] => [
  {
    key: 'newUsers',
    label: t('dashboard.series.newUsers'),
    color: 'var(--series-1)',
  },
  {
    key: 'activeUsers',
    label: t('dashboard.series.activeUsers'),
    color: 'var(--series-4)',
  },
];

/**
 * The front page.
 *
 * Ordered by what an operator opens it to find out, which for a wake-up
 * product is one thing: *did people get up this morning, and what did that
 * cost?* Everything above the fold answers that — the mornings first, then
 * what the day took at the till. The population and the breakdowns come after,
 * because they are context rather than news.
 *
 * Every figure above that stands for something people *did* is clickable, and
 * opens the list of the people behind it. A dashboard that can only be read is
 * a dashboard whose next question has to be asked somewhere else.
 */
export function DashboardPage() {
  const t = useT();
  const [params, setParams] = useSearchParams();

  // Which drill-down is open is part of the address, like every other record
  // in this console: a refresh, a bookmark or a link to a colleague all land
  // on the same list rather than on the page it was opened from.
  const metric = parseMetric(params.get('metric'));
  const scope: ActivityScope =
    params.get('scope') === 'window' ? 'window' : 'today';

  const drill: Drill = (next, nextScope = 'today') =>
    setParams({ metric: next, scope: nextScope });

  const overview = useQuery({
    queryKey: ['dashboard', TREND_DAYS],
    queryFn: () =>
      api.get<DashboardOverview>('/api/dashboard', {
        // "Today" must mean the operator's day, not the container's.
        tzOffset: timezoneOffsetMinutes(),
        days: TREND_DAYS,
      }),
    refetchInterval: 60_000,
  });

  if (overview.isPending) return <DashboardSkeleton />;
  if (overview.isError) return <ErrorNote error={overview.error} />;
  if (!overview.data) return null;

  const data = overview.data;

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.description', { days: TREND_DAYS })}
        action={
          <p className="text-xs text-ink-3">
            {t('dashboard.policy', {
              cost: formatCredits(data.policy.rewardAlarmCost),
              window: data.policy.rewardWindow,
              notice: data.policy.rewardMinNotice,
            })}
          </p>
        }
      />

      <div className="space-y-5">
        <NeedsAttention data={data} />
        <ThisMorning data={data} onDrill={drill} />
        <TodayInBusiness data={data} onDrill={drill} />
        <TheEconomy data={data} onDrill={drill} />

        <div className="grid gap-4 xl:grid-cols-2">
          <Card>
            <CardHeader
              title={t('dashboard.charts.wake')}
              description={t('dashboard.charts.wakeDescription', {
                days: TREND_DAYS,
              })}
            />
            <div className="p-4 sm:p-5">
              <TrendChart data={data.trend} series={wakeSeries(t)} />
            </div>
          </Card>

          <Card>
            <CardHeader
              title={t('dashboard.charts.credits')}
              description={t('dashboard.charts.creditsDescription')}
            />
            <div className="p-4 sm:p-5">
              <TrendChart data={data.trend} series={creditSeries(t)} />
            </div>
          </Card>
        </div>

        <Card>
          <CardHeader
            title={t('dashboard.population.title')}
            description={t('dashboard.population.description')}
          />
          <div className="p-4 sm:p-5">
            <TrendChart
              data={data.trend}
              series={growthSeries(t)}
              height="h-56"
            />
          </div>
          <PopulationRow data={data} />
        </Card>

        <div className="grid gap-4 xl:grid-cols-2">
          <BreakdownCard
            title={t('dashboard.breakdown.platforms')}
            description={t('dashboard.breakdown.platformsDescription')}
            rows={data.breakdowns.platforms}
            icon={<Smartphone size={14} />}
            format={formatPlatform}
            secondaryLabel={t('dashboard.breakdown.subscribed')}
          />
          <BreakdownCard
            title={t('dashboard.breakdown.challenges')}
            description={t('dashboard.breakdown.challengesDescription')}
            rows={data.breakdowns.challenges}
            icon={<AlarmClock size={14} />}
            secondaryLabel={t('dashboard.breakdown.won')}
          />
          <BreakdownCard
            title={t('dashboard.breakdown.countries')}
            description={t('dashboard.breakdown.countriesDescription')}
            rows={data.breakdowns.countries}
            icon={<UsersIcon size={14} />}
            secondaryLabel={t('dashboard.breakdown.subscribed')}
          />
          <BreakdownCard
            title={t('dashboard.breakdown.risk')}
            description={t('dashboard.breakdown.riskDescription', {
              days: TREND_DAYS,
            })}
            rows={data.breakdowns.risk}
            icon={<Gavel size={14} />}
            // Risk levels share their raw values with alarm event types, so
            // they get their own lookup rather than the shared `enum.*` one.
            format={(key) => t(`events.risk.${key}`)}
            emptyNote={t('dashboard.breakdown.riskEmpty')}
          />
        </div>
      </div>

      {metric && (
        // Keyed by what it shows, so opening another tile starts a fresh sheet
        // at its first page instead of inheriting the last one's.
        <ActivityDrawer
          key={`${metric}:${scope}`}
          metric={metric}
          scope={scope}
          days={TREND_DAYS}
          onClose={() => setParams({}, { replace: true })}
        />
      )}
    </>
  );
}

// ── Attention ───────────────────────────────────────────────────────────────

/**
 * The things waiting for a human, and nothing else.
 *
 * Rendered only when there is something in it. A banner that is always present
 * is a banner nobody reads, and the whole value of this strip is that its
 * appearance means something.
 */
function NeedsAttention({ data }: { data: DashboardOverview }) {
  const t = useT();
  const navigate = useNavigate();
  const { withheldClaims, overdueJobs } = data.attention;

  if (withheldClaims === 0 && overdueJobs === 0) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {withheldClaims > 0 && (
        <StatTile
          icon={<Gavel size={16} />}
          label={t('dashboard.attention.claims')}
          value={formatNumber(withheldClaims)}
          caption={t('dashboard.attention.claimsCaption')}
          tone="warn"
          onClick={() => void navigate('/alarms?tab=review')}
        />
      )}
      {overdueJobs > 0 && (
        <StatTile
          icon={<BellRing size={16} />}
          label={t('dashboard.attention.jobs')}
          value={formatNumber(overdueJobs)}
          caption={t('dashboard.attention.jobsCaption')}
          tone="warn"
          onClick={() => void navigate('/notifications')}
        />
      )}
    </div>
  );
}

// ── Today, commercially ─────────────────────────────────────────────────────

/**
 * Did today pay for itself?
 *
 * Four numbers, all since the operator's midnight: credits bought, people who
 * went Premium, cash the bank actually took, and how many new people arrived
 * to do any of it. They sit directly under the morning because that is the
 * order the day is read in — what happened, then what it was worth — and every
 * one of them is a *question*: clicking it names the people behind it.
 */
function TodayInBusiness({
  data,
  onDrill,
}: {
  data: DashboardOverview;
  onDrill: Drill;
}) {
  const t = useT();
  const { commerce, population } = data;

  return (
    <Card>
      <CardHeader
        title={t('dashboard.today.title')}
        description={t('dashboard.today.description')}
        action={
          <span className="text-xs text-ink-3">{t('dashboard.today.hint')}</span>
        }
      />

      <div className="grid grid-cols-2 gap-3 p-4 sm:gap-4 lg:grid-cols-4 sm:p-5">
        <StatTile
          icon={<HandCoins size={16} />}
          label={t('dashboard.today.toppedUp')}
          value={formatCompact(commerce.topUps.amount)}
          caption={t('dashboard.today.toppedUpCaption', {
            users: formatNumber(commerce.topUps.users),
            count: formatNumber(commerce.topUps.events),
          })}
          tone="brand"
          onClick={() => onDrill('top_up')}
        />
        <StatTile
          icon={<Crown size={16} />}
          label={t('dashboard.today.premium')}
          value={formatNumber(commerce.premium.users)}
          caption={t('dashboard.today.premiumCaption', {
            count: formatNumber(population.subscribedUsers),
          })}
          tone="brand"
          onClick={() => onDrill('premium')}
        />
        <StatTile
          icon={<Banknote size={16} />}
          label={t('dashboard.today.revenue')}
          value={formatMoneyCompact(commerce.revenue.amount)}
          caption={t('dashboard.today.revenueCaption', {
            count: formatNumber(commerce.revenue.events),
            users: formatNumber(commerce.revenue.users),
          })}
          onClick={() => onDrill('revenue')}
        />
        <StatTile
          icon={<UserPlus size={16} />}
          label={t('dashboard.today.newUsers')}
          value={formatNumber(population.newToday)}
          caption={t('dashboard.today.newUsersCaption', {
            count: formatNumber(population.newThisWeek),
          })}
          onClick={() => onDrill('new_user')}
        />
      </div>
    </Card>
  );
}

// ── This morning ────────────────────────────────────────────────────────────

function ThisMorning({
  data,
  onDrill,
}: {
  data: DashboardOverview;
  onDrill: Drill;
}) {
  const t = useT();
  const { today, period } = data;
  const settled = today.earned + today.missed;

  return (
    <Card>
      <CardHeader
        title={t('dashboard.morning.title')}
        description={t('dashboard.morning.description')}
        action={
          <span className="text-xs text-ink-3">
            {t('dashboard.morning.settled', {
              count: formatNumber(period.earned + period.missed),
              days: data.window.days,
            })}
          </span>
        }
      />

      <div className="grid grid-cols-2 gap-3 p-4 sm:gap-4 lg:grid-cols-4 sm:p-5">
        <StatTile
          icon={<Sunrise size={16} />}
          label={t('dashboard.morning.wokeUp')}
          value={formatNumber(today.earned)}
          caption={
            settled > 0
              ? t('dashboard.morning.wokeUpCaption', {
                  percent: percentOf(today.earned, settled),
                })
              : t('dashboard.morning.noRings')
          }
          tone="brand"
          onClick={() => onDrill('woke_up')}
        />
        <StatTile
          icon={<TrendingDown size={16} />}
          label={t('dashboard.morning.missed')}
          value={formatNumber(today.missed)}
          caption={
            settled > 0
              ? t('dashboard.morning.missedCaption', {
                  percent: percentOf(today.missed, settled),
                })
              : '—'
          }
          onClick={() => onDrill('missed')}
        />
        <StatTile
          icon={<Activity size={16} />}
          label={t('dashboard.morning.rings')}
          value={formatNumber(today.rings)}
          caption={t('dashboard.morning.ringsCaption', {
            count: formatNumber(today.timedOut),
          })}
        />
        <StatTile
          icon={<AlarmClock size={16} />}
          label={t('dashboard.morning.solve')}
          value={formatDuration(today.averageSolveMs || null)}
          caption={t('dashboard.morning.solveCaption')}
        />
      </div>

      {/* The wake rate as a bar: one number, but the one the product is about,
          so it gets a shape rather than another tile. */}
      <div className="border-t border-line px-4 py-3.5 sm:px-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-ink">
            {t('dashboard.morning.wakeRate', { days: data.window.days })}
          </span>
          <span className="tnum text-sm font-semibold text-ink">
            {formatRate(period.wakeRate)}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-raised">
          <div
            className="h-full rounded-full bg-[var(--series-1)]"
            style={{ width: `${Math.min(100, period.wakeRate * 100)}%` }}
          />
        </div>
        <p className="mt-1.5 text-xs text-ink-3">
          {t('dashboard.morning.breakdown', {
            earned: formatNumber(period.earned),
            missed: formatNumber(period.missed),
            withheld: formatNumber(period.withheld),
          })}
        </p>
      </div>
    </Card>
  );
}

// ── Economy ─────────────────────────────────────────────────────────────────

/**
 * Where the credits are and where they went.
 *
 * The three holdings come first because they are a *position* — money the
 * business is holding on users' behalf — and the flows after, because they are
 * what changed it.
 */
function TheEconomy({
  data,
  onDrill,
}: {
  data: DashboardOverview;
  onDrill: Drill;
}) {
  const t = useT();
  const { population, economy, payouts } = data;

  return (
    <Card>
      <CardHeader
        title={t('dashboard.economy.title')}
        description={t('dashboard.economy.description', {
          days: data.window.days,
        })}
      />

      <div className="grid grid-cols-2 gap-3 p-4 sm:gap-4 lg:grid-cols-4 sm:p-5">
        <StatTile
          icon={<Wallet size={16} />}
          label={t('dashboard.economy.held')}
          value={formatCompact(population.balance)}
          caption={t('dashboard.economy.heldCaption')}
        />
        <StatTile
          icon={<Lock size={16} />}
          label={t('dashboard.economy.staked')}
          value={formatCompact(population.blockedBalance)}
          caption={t('dashboard.economy.stakedCaption', {
            percent: percentOf(population.blockedBalance, population.balance),
          })}
        />
        <StatTile
          icon={<Coins size={16} />}
          label={t('dashboard.economy.won')}
          value={formatCompact(population.earnedBalance)}
          caption={t('dashboard.economy.wonCaption')}
        />
        <StatTile
          icon={<Landmark size={16} />}
          label={t('dashboard.economy.commission')}
          value={formatCompact(payouts.commission)}
          caption={t('dashboard.economy.commissionCaption', {
            count: payouts.runs,
          })}
          tone="brand"
        />
      </div>

      {/* The flows are the window's, not today's — so are the lists they
          open. */}
      <dl className="grid grid-cols-2 divide-line border-t border-line sm:grid-cols-4">
        <Flow
          label={t('dashboard.economy.toppedUp')}
          value={economy.toppedUp}
          onOpen={() => onDrill('top_up', 'window')}
        />
        <Flow
          label={t('dashboard.economy.forfeited')}
          value={economy.forfeited}
          onOpen={() => onDrill('forfeited', 'window')}
        />
        <Flow
          label={t('dashboard.economy.paidOut')}
          value={economy.payouts}
          onOpen={() => onDrill('payout', 'window')}
        />
        <Flow
          label={t('dashboard.economy.adjusted')}
          value={economy.adjusted}
          onOpen={() => onDrill('adjusted', 'window')}
        />
      </dl>

      {payouts.paid > 0 && (
        <p className="border-t border-line px-4 py-2.5 text-xs text-ink-3 sm:px-5">
          {t('dashboard.economy.shared', {
            amount: formatCredits(payouts.paid),
            count: payouts.recipients,
          })}
          {payouts.carryOver > 0 &&
            ` · ${t('dashboard.economy.carryOver', {
              amount: formatCredits(payouts.carryOver),
            })}`}
          .
        </p>
      )}
    </Card>
  );
}

/**
 * One credit flow over the window, and the people behind it.
 *
 * The wrapper carries the click rather than a button inside it: a definition
 * list may only contain `dt`/`dd` pairs and the `div`s that group them, so the
 * row is made operable the way the tables here already do it — role, tab stop
 * and the two keys a button would answer to.
 */
function Flow({
  label,
  value,
  onOpen,
}: {
  label: string;
  value: number;
  onOpen: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer border-b border-line px-4 py-3 transition-colors last:border-b-0 hover:bg-raised sm:px-5 sm:[&:nth-last-child(-n+4)]:border-b-0"
    >
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className="tnum mt-1 text-lg font-semibold text-ink">
        {formatNumber(value)}
      </dd>
    </div>
  );
}

// ── Population ──────────────────────────────────────────────────────────────

function PopulationRow({ data }: { data: DashboardOverview }) {
  const t = useT();
  const p = data.population;

  return (
    <div className="grid grid-cols-2 gap-3 border-t border-line p-4 sm:gap-4 lg:grid-cols-4 sm:p-5">
      {/* Today's arrivals are in the commerce card; this row is the standing
          position, and a Premium head-count belongs with it. */}
      <StatTile
        icon={<Crown size={16} />}
        label={t('dashboard.population.premium')}
        value={formatCompact(p.subscribedUsers)}
        caption={t('dashboard.population.premiumCaption', {
          percent: percentOf(p.subscribedUsers, p.users),
        })}
        tone="brand"
      />
      <StatTile
        icon={<UsersIcon size={16} />}
        label={t('dashboard.population.total')}
        value={formatCompact(p.users)}
        caption={t('dashboard.population.totalCaption', {
          count: formatNumber(p.activeToday),
        })}
      />
      <StatTile
        icon={<AlarmClock size={16} />}
        label={t('dashboard.population.liveAlarms')}
        value={formatCompact(p.enabledAlarms)}
        caption={t('dashboard.population.liveAlarmsCaption', {
          total: formatNumber(p.alarms),
          reward: formatNumber(p.rewardAlarms),
        })}
      />
      <StatTile
        icon={<BellRing size={16} />}
        label={t('dashboard.population.pushable')}
        value={formatCompact(p.pushableUsers)}
        caption={t('dashboard.population.pushableCaption', {
          percent: percentOf(p.pushableUsers, p.users),
        })}
      />
    </div>
  );
}

// ── Breakdowns ──────────────────────────────────────────────────────────────

/**
 * A grouped count, drawn as proportion bars rather than a pie.
 *
 * Bars share a baseline, so comparing two categories is a length comparison
 * rather than an angle one — and the numbers are printed beside them anyway,
 * which is the relief the light-mode palette contrast obliges.
 */
function BreakdownCard({
  title,
  description,
  rows,
  icon,
  format = humanize,
  secondaryLabel,
  emptyNote,
}: {
  title: string;
  description: string;
  rows: BreakdownRow[];
  icon: ReactNode;
  format?: (key: string) => string;
  secondaryLabel?: string;
  emptyNote?: string;
}) {
  const t = useT();
  const max = Math.max(1, ...rows.map((row) => row.total));

  return (
    <Card>
      <CardHeader title={title} description={description} />
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-ink-3 sm:px-5">
          {emptyNote ?? t('dashboard.breakdown.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((row) => (
            <li key={row.key} className="px-4 py-2.5 sm:px-5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-center gap-2 text-sm text-ink">
                  <span className="shrink-0 text-ink-3">{icon}</span>
                  <span className="truncate">{format(row.key)}</span>
                </span>
                <span className="tnum shrink-0 text-sm font-semibold text-ink">
                  {formatNumber(row.total)}
                  {secondaryLabel && row.secondary > 0 && (
                    <span className="ml-1.5 text-xs font-normal text-ink-3">
                      · {formatNumber(row.secondary)} {secondaryLabel}
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-raised">
                <div
                  className={cn('h-full rounded-full bg-[var(--series-1)]')}
                  style={{ width: `${(row.total / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function DashboardSkeleton() {
  const t = useT();

  return (
    <>
      <PageHeader title={t('dashboard.title')} />
      <div className="space-y-5">
        <Skeleton className="h-52" />
        <Skeleton className="h-44" />
        <Skeleton className="h-56" />
        <div className="grid gap-4 xl:grid-cols-2">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    </>
  );
}
